import type { PurchaseOrder, PurchaseOrderLine } from "@/api/purchase_order.tsx"
import { format_units } from "@/purchase_orders/purchase_orders_display.ts"
import { exactCurrencyFormatter } from "@/utils"

/** An order's lines as the customer wrote them: what was ordered, how much, at what price. */
export function PurchaseOrderLines({ order, className }: { order: PurchaseOrder; className?: string }) {
  const lines = order.lines ?? []
  return (
    <div class={`overflow-x-auto ${className ?? ""}`}>
      <table class="w-full min-w-[640px] text-xs">
        <thead>
          <tr class="text-left text-slate-500 border-b">
            <th class="py-1 pr-3 font-medium">#</th>
            <th class="py-1 pr-3 font-medium">Item</th>
            <th class="py-1 pr-3 font-medium">Description</th>
            <th class="py-1 pr-3 font-medium">UPC</th>
            <th class="py-1 pr-3 font-medium">Customer item</th>
            <th class="py-1 pr-3 font-medium text-right">Ordered</th>
            <th class="py-1 pr-3 font-medium text-right">Unit price</th>
            <th class="py-1 font-medium text-right">Extended</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line: PurchaseOrderLine) => (
            <tr key={line.id} class="border-b last:border-0">
              <td class="py-1 pr-3 text-slate-500">{line.line_number}</td>
              <td class="py-1 pr-3 font-mono">{line.item_number ?? "—"}</td>
              <td class="py-1 pr-3">{line.description ?? "—"}</td>
              <td class="py-1 pr-3 font-mono text-slate-600">{line.upc ?? "—"}</td>
              <td class="py-1 pr-3 font-mono text-slate-600">{line.buyer_item_number ?? "—"}</td>
              <td class="py-1 pr-3 text-right tabular-nums">
                {format_units(line.quantity)} {line.uom ?? ""}
                {line.case_pack ? <span class="text-slate-400"> × {line.case_pack}</span> : null}
              </td>
              <td class="py-1 pr-3 text-right tabular-nums">{line.unit_price !== null ? exactCurrencyFormatter(line.unit_price) : "—"}</td>
              <td class="py-1 text-right tabular-nums">{line.extended_price !== null ? exactCurrencyFormatter(line.extended_price) : "—"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr class="font-medium">
            <td colSpan={5} class="py-1 pr-3 text-right text-slate-500">
              Order total
            </td>
            <td class="py-1 pr-3 text-right tabular-nums">{format_units(order.total_quantity)}</td>
            <td />
            <td class="py-1 text-right tabular-nums">{order.total_amount !== null ? exactCurrencyFormatter(order.total_amount) : "—"}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/** The header facts a dispute turns on, as label/value pairs. */
export function PurchaseOrderFacts({ order, className }: { order: PurchaseOrder; className?: string }) {
  const facts: [string, string | null][] = [
    ["Ship to", order.ship_to_address ? order.ship_to_address.split("\n").join(", ") : null],
    ["Requested ship date", order.requested_ship_date],
    ["FOB", order.fob],
    ["Customer's number for you", order.vendor_number],
    ["Purpose", order.purpose],
    ...order.allowances.map((a): [string, string | null] => [
      "Allowance on the order",
      `${a.description ?? a.code ?? "Allowance"}${a.percent !== null ? ` ${a.percent}%` : ""}${a.amount !== null ? ` (${exactCurrencyFormatter(a.amount)})` : ""}`,
    ]),
  ]
  const shown = facts.filter(([, value]: [string, string | null]) => value !== null && value.trim() !== "")
  if (shown.length === 0) {
    return null
  }
  return (
    <dl class={`grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs ${className ?? ""}`}>
      {shown.map(([label, value]: [string, string | null], i: number) => (
        <>
          <dt key={`l${i}`} class="text-slate-500">
            {label}
          </dt>
          <dd key={`v${i}`}>{value}</dd>
        </>
      ))}
    </dl>
  )
}
